import { useQuery } from "@tanstack/react-query";
import { GradeContributionEvidence } from "@/components/gradebook/GradeContributionEvidence";
import { useAuth } from "@/contexts/AuthContext";
import { assignmentsApi } from "@/lib/api/assignments";
import type { VisitLevel } from "@/lib/visit/address";
import type { VisitResolution } from "@/lib/visit/registry";

/** Feedback's second address record is its exact released event. Backend reads
 * authorize the actor; this resolver verifies the immutable identity join. */
export function useFeedbackVisit(level: VisitLevel | null): VisitResolution | null {
  const { me } = useAuth();
  const enabled = Boolean(level?.object === "feedback" && level.job && me?.tenant_id && me.user_id);
  const read = useQuery({
    queryKey: ["released-feedback-evidence", me?.tenant_id, me?.user_id, level?.id, level?.job],
    enabled, retry: false,
    queryFn: async () => {
      const attempt = await assignmentsApi.getSubmission(level!.id);
      if (attempt.id !== level!.id) throw Error("Exact evidence unavailable");
      const [assignment, events] = await Promise.all([assignmentsApi.get(attempt.assignment_id), assignmentsApi.listFeedback(attempt.id)]);
      const version = assignment.versions.find(value => value.id === attempt.assignment_version_id);
      const feedback = events.find(value => value.id === level!.job);
      if (assignment.id !== attempt.assignment_id || assignment.course_id !== attempt.course_id || !version || !feedback
        || feedback.submission_id !== attempt.id || feedback.visible_at === null || Date.parse(feedback.visible_at) > Date.now()
        || !["graded", "complete", "missing"].includes(feedback.outcome)
        || attempt.attempt_terms && attempt.attempt_terms.version_id !== version.id) throw Error("Exact evidence unavailable");
      if (feedback.selected_scale_row_id) {
        const selected = version.scale?.rows.find(row => row.id === feedback.selected_scale_row_id);
        if (!selected || Number(selected.score) !== Number(feedback.score)) throw Error("Original scale evidence unavailable");
      }
      return { attempt, version, feedback };
    },
  });
  if (!level) return null;
  if (!enabled || read.isError) return { status: "refused" };
  if (!read.data) return { status: "loading" };
  const evidence = read.data;
  return { status: "ready", subject: evidence.version.title, boxes: [{
    kind: "fact", id: `feedback-evidence-${evidence.feedback.id}`, label: "Released coursework evidence",
    job: "Read the exact attempt and released judgement contributing to this result", owner: { system: "imam" },
    fold: "never", read: { status: "ready", value: evidence, readAt: evidence.feedback.created_at }, detail: () => "",
    body: () => <GradeContributionEvidence {...evidence} />,
  }] };
}
